import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";
import { PLAYER_CAPSULE } from "../scene/player.ts";
import { createAstronautMesh } from "../scene/astronaut.ts";
import { applyInstanceTint } from "../render/instanceTint.ts";
import { type InputRecording } from "./inputRecorder.ts";
import {
  EMPTY_MILESTONE_RECORDING,
  type MilestoneRecording,
} from "./milestone.ts";
import {
  advanceReplay,
  createReplayState,
  type ReplayMode,
  type ReplayState,
} from "./replayController.ts";
import { FIXED_STEP_SECONDS } from "./simulationStep.ts";
import type { InstanceId } from "./instanceId.ts";
import {
  INITIAL_CONSCIOUSNESS,
  type Consciousness,
} from "./knockoutState.ts";
import {
  createThoughtBubble,
  type ThoughtBubble,
} from "../render/thoughtBubble.ts";

/**
 * Ghost replay body (REQ-001 / REQ-002 deepening).
 *
 * A ghost is a separate Three.js mesh plus Rapier dynamic capsule whose planar
 * velocity each fixed simulation step is taken from a frozen `InputRecording`
 * via `replayAtTick`. The ghost owns its own tick counter that advances by
 * exactly one per call to `advanceTick`, so the host (`src/app.ts`) can drive
 * every active ghost from the same fixed-step loop that already advances
 * physics and `TimeOfDay`.
 *
 * Past the end of the recording, `replayAtTick` returns a zero vector, so the
 * ghost simply stops moving. Despawn semantics are NOT in scope for this slice;
 * deletion is tied to portal traversal and lands with REQ-003.
 *
 * The ghost's mesh is tinted once at spawn with `applyInstanceTint` so it reads
 * as a different generation than the active player. The tint is a one-shot
 * stamp; it does not update over the day cycle.
 *
 * NOT in scope:
 *   - Portal traversal (REQ-003).
 *   - Despawn / cleanup of finished ghosts.
 *   - Multi-ghost scheduling driven by timeline state.
 *   - Heading-aware movement (input is world-axis-aligned, matching the
 *     active player).
 */

/** Minimal subset of `RAPIER.World` the ghost factory needs, so tests can pass
 * a real world or a stub. */
export interface GhostWorldHandle {
  createRigidBody: RAPIER.World["createRigidBody"];
  createCollider: RAPIER.World["createCollider"];
}

export interface GhostInstance {
  mesh: THREE.Mesh;
  body: RAPIER.RigidBody;
  /** Origin normalized time-of-day in [0, 1] used for the constant tint. */
  originNormalized: number;
  /**
   * Generation index of the instance this ghost is replaying (REQ-007). Set
   * once at spawn from the OUTGOING active player's `instanceId` at the
   * moment of traversal: the ghost IS that closed-out instance, just rendered
   * as a recording. Carried so a future thought-bubble UI (REQ-032) can label
   * the ghost via `formatInstanceId`. The label is data only this slice.
   */
  readonly instanceId: InstanceId;
  /**
   * Frozen input recording driving this ghost's playback. Exposed so the
   * host can read the punch flag at the ghost's current tick via
   * `replayPunchAtTick(recording, tickIndex)` for the per-tick punch
   * resolver (REQ-033 partial). The recording is deeply frozen at
   * snapshot time (`InputRecorder.snapshot`) so reading it is safe; the
   * recorder cannot mutate it after capture.
   */
  readonly recording: InputRecording;
  /**
   * Frozen milestone recording captured during the lifetime that produced
   * this ghost (F-013 PR3a). Empty for ghosts spawned from a lifetime
   * that fired no milestones (e.g. Act 1 cinematic actors whose
   * hand-authored recordings predate the milestone system). PR3b's
   * hybrid replay path-follower steers toward these milestones; this
   * slice only stores them.
   */
  readonly milestones: MilestoneRecording;
  /**
   * Absolute tick within the ghost's home timeline at which the ghost's
   * recording starts (F-014 Reading C). On arrival at this timeline at
   * tick `T`, the ghost's playback state is at relative tick `T - startTick`.
   * A ghost with `startTick = 0` records from the very start of its
   * timeline (matches the pre-F-014 default and the Act 1 cinematic
   * actors filed at boot before any player visit). Frozen at creation.
   */
  readonly startTick: number;
  /**
   * Camera input yaw applied when this ghost turns recorded keys into
   * replay velocity. Scripted ghosts keep `0`; player-recorded ghosts use
   * the production camera yaw so preview and replay agree.
   */
  readonly replayYawRad: number;
  /**
   * Fast-forward the ghost to absolute tick `absoluteTick` within its
   * home timeline (F-014 Reading C). Virtually replays the recording
   * from tick 0 to `relativeTick = absoluteTick - startTick`, advancing
   * the controller's milestone state and snapping the Rapier body to
   * the resulting position. No-op when `absoluteTick <= startTick`
   * (ghost has not started yet from this arrival's POV). The caller
   * (typically the registry's `setActiveTimeline` path) is responsible
   * for despawning ghosts whose door_traversal milestone fires before
   * `absoluteTick` BEFORE invoking this method; this method itself
   * does not consult lit-state.
   */
  fastForwardTo: (absoluteTick: number) => void;
  /**
   * Two-state consciousness flag (REQ-033 partial). A ghost opens at
   * `'conscious'` regardless of how its recording resolved in the source
   * timeline; the per-tick punch resolver in the host can flip this to
   * `'unconscious'` if a punch lands on this ghost in the active timeline.
   * On `reset()` the flag returns to `'conscious'` so each timeline visit
   * is a fresh playback (a previously knocked-out ghost is conscious
   * again on re-entry).
   */
  consciousness: Consciousness;
  /**
   * Thought-bubble overlay (REQ-032). Each ghost owns its own bubble so the
   * host can call `setIcon` once per render frame after the lookahead scan
   * resolves the next qualitatively different action. The bubble's group
   * mesh is parented to the same scene as the ghost mesh; `update` is
   * called per render frame to position and billboard it. Opens hidden
   * (no current kind, group `visible` false). `reset()` hides the bubble
   * so a re-entered timeline starts with a clean preview.
   */
  readonly thoughtBubble: ThoughtBubble;
  /** Number of `advanceTick` calls applied so far. Starts at 0. */
  readonly tickIndex: number;
  /**
   * Most recent replay mode the hybrid controller used (F-013 PR3b).
   * `'replaying-input'` while the ghost is following the recorded input
   * within drift threshold; `'path-following'` while the ghost has
   * drifted off course and is steering toward the next pending
   * milestone. Reset to `'replaying-input'` on `reset()`.
   */
  readonly replayMode: ReplayMode;
  /**
   * Advance the ghost one fixed step: read the recording at the current tick,
   * write the resulting planar velocity onto the body (preserving y), and
   * increment the internal tick counter. After the recording is exhausted
   * this writes a zero planar velocity each call, so the ghost decelerates
   * to a stop under linear damping.
   */
  advanceTick: () => void;
  /** Copy the body's translation onto the mesh; call once per render frame. */
  syncMeshFromBody: () => void;
  /**
   * Rewind the ghost to its initial state: tick counter back to 0, body
   * translated back to the spawn position with zero linear velocity, and the
   * mesh resnapped onto the body. Used by `TimelineRegistry` when the active
   * player re-enters the timeline this ghost was recorded in: each visit is
   * a fresh playback (REQ-001 / REQ-003 deepening). The model is "the ghost
   * starts walking again the moment you arrive at its timeline."
   */
  reset: () => void;
}

export interface CreateGhostOptions {
  recording: InputRecording;
  /**
   * Frozen milestone recording for this ghost (F-013 PR3a). Optional;
   * defaults to an empty recording for callers that predate the
   * milestone system (Act 1 cinematic actors, ghost test fixtures).
   * PR3b's hybrid replay reads this to steer toward the next milestone.
   */
  milestones?: MilestoneRecording;
  /**
   * Absolute start tick within the ghost's home timeline (F-014).
   * Defaults to `0` so existing callers (Act 1 cinematic, ghost test
   * fixtures, pre-F-014 traversal paths) file ghosts at the start of
   * the destination timeline. The traversal handler in `portalTraversal.ts`
   * passes the active timeline's tick clock at the moment of filing so
   * a ghost's playback state aligns with the timeline it was recorded in.
   */
  startTick?: number;
  /**
   * Normalized time-of-day in [0, 1] used to tint the ghost's mesh via
   * `applyInstanceTint`. Typically the `TimeOfDay.normalized()` reading at
   * the moment the ghost was spawned (which represents the recording's
   * origin from the host's perspective).
   */
  originNormalized: number;
  /**
   * Generation index of the instance this ghost is replaying (REQ-007). The
   * caller passes the OUTGOING active player's `instanceId` at the moment of
   * traversal so the ghost preserves its identity across timeline switches
   * (a returning player still sees the same You-1 / You-2 in its source
   * timeline). See `formatInstanceId` for the GDD-canonical display label.
   */
  instanceId: InstanceId;
  scene: THREE.Scene;
  world: GhostWorldHandle;
  /** World-space spawn position. The capsule center y is computed so the base
   * sits on y=0 regardless of the supplied y; only x and z are used. */
  startPosition: { x: number; z: number };
  /**
   * Camera input yaw applied to recorded velocities at replay time.
   * Defaults to `0` (world-axis behavior) so scripted cinematic
   * recordings and test fixtures keep their existing semantics. The
   * production traversal handler (`src/sim/portalTraversal.ts`)
   * passes `CAMERA_INPUT_YAW_RAD` explicitly so a real player's
   * recording replays along the same rotated path the player walked
   * (preserving the time-loop invariant).
   */
  yawRad?: number;
}

/**
 * Build a ghost replay astronaut mesh. Its capsule collider matches the active
 * player's dimensions so a recorded path produces visually identical motion.
 * The ghost is dynamic (not kinematic) so it interacts with the same colliders
 * the active player does; that keeps the door-blocking behavior consistent
 * with REQ-002 ("worked around or physically redirected").
 */
export function createGhost(options: CreateGhostOptions): GhostInstance {
  const {
    recording,
    milestones = EMPTY_MILESTONE_RECORDING,
    startTick = 0,
    originNormalized,
    instanceId,
    scene,
    world,
    startPosition,
    yawRad = 0,
  } = options;
  const { radius, cylinderLength } = PLAYER_CAPSULE;

  const mesh = createAstronautMesh({ radius, cylinderLength, name: "ghost" });
  applyInstanceTint(mesh, originNormalized);
  scene.add(mesh);

  const restY = cylinderLength / 2 + radius;

  const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
    .setTranslation(startPosition.x, restY, startPosition.z)
    .enabledRotations(false, true, false)
    .setLinearDamping(8.0);

  const body = world.createRigidBody(bodyDesc);

  const colliderDesc = RAPIER.ColliderDesc.capsule(
    cylinderLength / 2,
    radius,
  ).setFriction(0.5);
  world.createCollider(colliderDesc, body);

  // REQ-032: each ghost owns one thought-bubble. The bubble starts hidden
  // (`setIcon(null)` is the implicit initial state inside `createThoughtBubble`).
  // The host's per-render loop reads the lookahead result and calls
  // `setIcon` on the matching kind.
  const thoughtBubble = createThoughtBubble(scene);

  // Mutable counter behind a getter on the returned object so the tick index
  // is observable but not externally writable. `advanceTick` is the only
  // mutation site, which makes ordering of replay calls verifiable.
  // F-013 PR3b: the controller tracks the milestone index, the running
  // expected position, and the most recent replay mode (input vs path).
  // `tickIndex` is mirrored from `replayState.tickIndex` so existing
  // call sites that read `ghost.tickIndex` keep working.
  let replayState: ReplayState = createReplayState(startPosition);
  // REQ-033 partial: ghosts open conscious. The host's punch resolver
  // mutates this through the returned object; `reset()` returns it to the
  // seed so each timeline visit is a fresh playback.
  let consciousness: Consciousness = INITIAL_CONSCIOUSNESS;

  const advanceTick = (): void => {
    // F-013 PR3b: hybrid replay. The controller picks between writing
    // the recorded velocity (default) or steering toward the next
    // pending milestone (when drift exceeds DRIFT_THRESHOLD), and
    // skips stale low-weight milestones. With an empty milestone log
    // the controller naturally stays in input-replay mode forever
    // (the path-follow branch is gated on a pending milestone).
    const t = body.translation();
    const result = advanceReplay(
      replayState,
      recording,
      milestones.milestones,
      { x: t.x, z: t.z },
      FIXED_STEP_SECONDS,
      yawRad,
    );
    const current = body.linvel();
    body.setLinvel(
      { x: result.velocity.x, y: current.y, z: result.velocity.z },
      true,
    );
    replayState = result.state;
  };

  const syncMeshFromBody = (): void => {
    const t = body.translation();
    mesh.position.set(t.x, t.y, t.z);
  };

  const fastForwardTo = (absoluteTick: number): void => {
    // F-014 Reading C: virtually replay through the controller from
    // tick 0 to `relativeTick`, integrating velocity * dt into a
    // virtual position. The Rapier body is snapped to the resulting
    // position at the end; no physics steps run during the fast-forward
    // so the path matches the milestone-aware unobstructed trajectory.
    // A negative relativeTick (caller arriving before this ghost's
    // startTick) is a no-op because the ghost has not started yet from
    // this arrival's POV.
    const relativeTick = absoluteTick - startTick;
    if (relativeTick <= 0) {
      replayState = createReplayState(startPosition);
      body.setTranslation(
        { x: startPosition.x, y: restY, z: startPosition.z },
        true,
      );
      body.setLinvel({ x: 0, y: 0, z: 0 }, true);
      syncMeshFromBody();
      return;
    }
    let virtualX = startPosition.x;
    let virtualZ = startPosition.z;
    let state = createReplayState(startPosition);
    for (let i = 0; i < relativeTick; i += 1) {
      const result = advanceReplay(
        state,
        recording,
        milestones.milestones,
        { x: virtualX, z: virtualZ },
        FIXED_STEP_SECONDS,
        yawRad,
      );
      virtualX += result.velocity.x * FIXED_STEP_SECONDS;
      virtualZ += result.velocity.z * FIXED_STEP_SECONDS;
      state = result.state;
    }
    replayState = state;
    body.setTranslation({ x: virtualX, y: restY, z: virtualZ }, true);
    body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    syncMeshFromBody();
  };

  const reset = (): void => {
    // F-013 PR3b: rewind the replay controller back to its tick-0
    // starting state alongside the body and consciousness reset. The
    // ghost's milestones index returns to 0 so every timeline visit
    // re-walks the milestone sequence from the top.
    replayState = createReplayState(startPosition);
    // REQ-033 partial: re-entering a timeline is a fresh playback (REQ-001 /
    // REQ-003). Reset consciousness to the seed so a ghost that was knocked
    // out during a prior visit is conscious again on return.
    consciousness = INITIAL_CONSCIOUSNESS;
    body.setTranslation(
      { x: startPosition.x, y: restY, z: startPosition.z },
      true,
    );
    body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    syncMeshFromBody();
    // REQ-032: hide the thought bubble on reset so a re-entered timeline
    // starts with no preview. The host re-resolves the lookahead on the
    // next render frame and re-shows the matching icon if appropriate.
    thoughtBubble.setIcon(null);
  };

  syncMeshFromBody();

  return {
    mesh,
    body,
    originNormalized,
    instanceId,
    recording,
    milestones,
    startTick,
    replayYawRad: yawRad,
    thoughtBubble,
    get tickIndex(): number {
      return replayState.tickIndex;
    },
    get replayMode(): ReplayMode {
      return replayState.lastMode;
    },
    get consciousness(): Consciousness {
      return consciousness;
    },
    set consciousness(next: Consciousness) {
      consciousness = next;
    },
    advanceTick,
    syncMeshFromBody,
    reset,
    fastForwardTo,
  };
}
